/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';

import FormInput from '../../components/forms/form-input';
import FormSelect from '../../components/forms/form-select';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import ScreenHeader from '../../components/common/screen-header';
import { JOB_NAMES } from '../../constants/jobNames';

import { ministriesService } from '../../services/ministries.service';
import { projectsService } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';

const ministrySchema = z.object({
  name: z.string().min(1, 'Name is required').max(120, 'Must be ≤ 120 characters').trim(),
  logoPath: z.string().max(500, 'Must be ≤ 500 characters').trim().optional().or(z.literal('')),
  appId: z.string().trim().optional().or(z.literal('')),
});

export type MinistryFormData = z.infer<typeof ministrySchema>;

const MinistryForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'ministries',
    basePath: '/ministries',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<MinistryFormData>({
    resolver: zodResolver(ministrySchema) as Resolver<MinistryFormData>,
    defaultValues: {
      name: '',
      logoPath: '',
      appId: '',
    },
    mode: 'onChange',
  });

  // Projects the caller may create in (platform admin = all, else own).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({
    value: p._id,
    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
  }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  const { data: existing, isLoading: fetchLoading } = useQuery({
    queryKey: ['ministries', id],
    queryFn: async () => await ministriesService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  useBreadcrumbTitle(isEditMode ? existing?.name : null);

  useEffect(() => {
    if (existing && isEditMode) {
      reset({
        name: existing.name || '',
        logoPath: existing.logoPath || '',
        appId: (existing as any).appId || '',
      });
    }
  }, [existing, isEditMode, reset]);

  const createMut = useMutation({
    mutationFn: (payload: any) => ministriesService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ministries'] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Ministry created successfully', life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: extractErrorMessage(error, 'Failed to save Ministry'),
        life: 10000,
      });
    },
  });

  const updateMut = useMutation({
    mutationFn: (payload: any) => ministriesService.update(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ministries'] });
      queryClient.invalidateQueries({ queryKey: ['ministries', id] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Ministry updated successfully', life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: extractErrorMessage(error, 'Failed to save Ministry'),
        life: 10000,
      });
    },
  });

  const isSaving = createMut.isPending || updateMut.isPending;

  const onSubmit = (data: MinistryFormData) => {
    const payload: { name: string; logoPath?: string; appId?: string } = {
      name: data.name,
    };

    // Owning project (honoured by the BE only for callers who can access it).
    if (data.appId) payload.appId = data.appId;

    if (data.logoPath && data.logoPath.trim()) {
      payload.logoPath = data.logoPath;
    }

    if (isEditMode) updateMut.mutate(payload);
    else createMut.mutate(payload);
  };

  if (fetchLoading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <PermissionGuard jobName={JOB_NAMES.MINISTRIES}>
      <Toast ref={toast} position="top-right" />

      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900" data-testid="SCR-Ministry-Form">
        <ScreenHeader
          icon="pi pi-building"
          title={isEditMode ? 'Edit Ministry' : 'Create Ministry'}
          subtitle={isEditMode ? 'Update ministry information' : 'Add a new ministry'}
          onBack={() => handleNavigate('/ministries')}
        />

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="ds-card p-4 mb-3">
            <h2 className="text-base font-semibold text-ink mb-3">
              Ministry Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                    placeholder="Enter ministry name"
                  />
                )}
              />

              <Controller
                name="logoPath"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="Logo Path"
                    error={!!errors.logoPath}
                    helperText={errors.logoPath?.message}
                    placeholder="Enter logo path (optional)"
                  />
                )}
              />

              {/* Owning project */}
              <Controller
                name="appId"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect
                    {...field}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Project"
                    options={projectOptions}
                    placeholder="Select project"
                    disabled={projectLocked || isEditMode}
                    error={!!errors.appId}
                    helperText={errors.appId?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <Button
              label="Cancel"
              severity="secondary"
              outlined
              onClick={() => handleNavigate('/ministries')}
              disabled={isSaving}
            />
            <Button
              type="submit"
              label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              icon={isSaving ? 'pi pi-spin pi-spinner' : undefined}
              disabled={isSaving}
            />
          </div>
        </form>
      </div>

      <DiscardChangesDialog
        visible={showLeaveDialog}
        onStay={cancelLeave}
        onLeave={confirmLeave}
        testId="MinistryForm.Dialog.Leave"
      />
    </PermissionGuard>
  );
};

export default MinistryForm;
